'use client';

import React, { useState } from 'react';

const investorTypes = [
  'Angel Investor',
  'Family Office',
  'Venture Capital',
  'Corporate Investor',
  'Strategic Investor',
  'Fund Manager',
  'Other',
];
const stagePrefs = ['Pre-Seed', 'Seed', 'Series A', 'Series B+', 'Growth', 'All Stages'];

export default function InvestorForm() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    organisation: '',
    investorType: '',
    sectors: '',
    stages: '',
    geography: '',
    ticketSize: '',
    thesis: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bento-card rounded-3xl p-12 text-center">
        <div className="w-16 h-16 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-primary"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-3">Application Received</h3>
        <p className="text-sm text-muted-foreground font-light leading-relaxed max-w-md mx-auto">
          Thank you for your interest in joining the Aregn Capital investor network. We will be in
          touch to discuss next steps.
        </p>
      </div>
    );
  }

  return (
    <div className="bento-card rounded-3xl p-8 sm:p-10">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-foreground mb-2">Join the Investor Network</h2>
        <p className="text-sm text-muted-foreground font-light">
          Tell us about your investment preferences so we can match you with relevant opportunities.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Organisation / Fund
            </label>
            <input
              type="text"
              name="organisation"
              value={formData.organisation}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="Organisation or fund name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Investor Type *
            </label>
            <select
              name="investorType"
              required
              value={formData.investorType}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
            >
              <option value="">Select type</option>
              {investorTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Preferred Sectors
            </label>
            <input
              type="text"
              name="sectors"
              value={formData.sectors}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="e.g. FinTech, SaaS, AI"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Preferred Stage
            </label>
            <select
              name="stages"
              value={formData.stages}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground focus:outline-none focus:border-primary/50 transition-colors"
            >
              <option value="">Select stage preference</option>
              {stagePrefs.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Geography
            </label>
            <input
              type="text"
              name="geography"
              value={formData.geography}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="e.g. UK, Europe, Global"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
              Typical Ticket Size
            </label>
            <input
              type="text"
              name="ticketSize"
              value={formData.ticketSize}
              onChange={handleChange}
              className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              placeholder="e.g. $25K–$250K"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Investment Thesis / Strategic Interests
          </label>
          <textarea
            name="thesis"
            value={formData.thesis}
            onChange={handleChange}
            rows={3}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="Describe your investment thesis and what types of opportunities interest you most..."
          />
        </div>

        <div>
          <label className="block text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            Anything Else?
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            className="w-full bg-input border border-primary/20 rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="Any additional context about your investment approach or preferences..."
          />
        </div>

        <button
          type="submit"
          className="w-full py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300 hover:scale-[1.02] active:scale-100"
        >
          Join Investor Network
        </button>
      </form>
    </div>
  );
}
